/**
 * CSS editorial compartido.
 *
 * Vive en un paquete y no en `global.css` a proposito: admin y web necesitan
 * exactamente los mismos estilos para que el preview sea fiel. Los tokens de
 * marca (`--color-brand-*`, `--font-display`…) los define cada app desde su
 * propio design system; aqui solo se usan, nunca se redefinen.
 *
 * Todos los valores vienen de custom properties con fallback. Cada app declara
 * esas variables con sus propios tokens de marca: la web las mapea a navy/gold
 * (ver `apps/web/src/styles/rich-content.css`) y el admin a los suyos. Asi este
 * paquete no conoce la identidad de Vimos y no hay que cambiarlo para
 * re-tematizar.
 *
 * Decisiones de diseno editorial que conviene no revertir:
 *
 * 1. **Los estilos viven bajo `.rc-prose`**, el contenedor del articulo. Nada
 *    aqui afecta al resto del sitio ni a las tarjetas del listado.
 * 2. **Las listas declaran `list-style` explicitamente.** Tailwind (preflight)
 *    y varios resets globales ponen `list-style: none` en `ul`/`ol`; sin esta
 *    declaracion las vinetas no existen. Ademas `ul` NO usa flex: los hijos de
 *    un flex container se "blockifican" y el `::marker` desaparece.
 * 3. **Los margenes son por elemento**, no el `> * + *` generico: los
 *    encabezados necesitan mas aire arriba que los parrafos entre si, y las
 *    listas deben quedar compactas pero agrupadas.
 * 4. **Los tamaños usan `clamp`** para que el mismo valor sirva en movil y en
 *    escritorio sin media queries duplicadas.
 */
export const RICH_CONTENT_CSS = `
.rc-prose {
  color: var(--rc-text, #1f2937);
  font-size: clamp(1rem, 0.95rem + 0.25vw, 1.0625rem);
  line-height: 1.7;
}
.rc-prose > * { margin-top: 0; margin-bottom: 0; }
.rc-prose > :first-child { margin-top: 0; }

/* Párrafos: 14px de aire debajo; los margenes colapsan con el top del
   siguiente bloque, asi que nunca se suman espacios. */
.rc-prose > p { margin-bottom: 0.875rem; }
.rc-prose > p:last-child { margin-bottom: 0; }

/* Encabezados: escalonado claro H1 (~40px) → H2 (20–24px) → H3 (17–19px)
   → cuerpo (16–17px). text-wrap: balance evita titulares con una sola
   palabra suelta en la última linea. */
.rc-h2, .rc-h3 {
  font-family: var(--rc-font-display, inherit);
  color: var(--rc-heading, #0f172a);
  line-height: 1.3;
  /* 7rem = 112px: el header del sitio es fixed con main pt-24 sm:pt-28;
     sin este margen los anchors del índice quedarían debajo del header. */
  scroll-margin-top: 7rem;
  text-wrap: balance;
}
.rc-prose .rc-h2 {
  margin-top: 2rem;
  margin-bottom: 0.75rem;
  font-weight: 700;
  font-size: clamp(1.25rem, 1.15rem + 0.5vw, 1.5rem);
}
.rc-prose .rc-h3 {
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
  font-size: clamp(1.0625rem, 1rem + 0.25vw, 1.1875rem);
}
.rc-prose > .rc-h2:first-child,
.rc-prose > .rc-h3:first-child { margin-top: 0; }

/* El cuerpo del texto no se pone en negrita al 700: 600 destaca sin
   oscurecer el parrafo entero. */
.rc-prose strong { font-weight: 600; }

.rc-prose a {
  color: var(--rc-link, #2563eb);
  text-decoration: underline;
  text-underline-offset: 3px;
}
.rc-prose a:hover { color: var(--rc-link-hover, #1d4ed8); }

/* Listas: marcador visible, items agrupados y sangria que alinea el texto
   envuelto con la primera linea. list-style-position externo es el que
   permite que ::marker se pinte. */
.rc-prose ul.rc-list {
  list-style: disc;
  padding-left: 1.5rem;
  margin: 0.75rem 0 0.875rem;
}
.rc-prose ol.rc-list {
  list-style: decimal;
  padding-left: 1.75rem;
  margin: 0.75rem 0 0.875rem;
}
.rc-prose ul.rc-list ul.rc-list { list-style: circle; }
.rc-prose ul.rc-list ul.rc-list ul.rc-list { list-style: square; }
.rc-prose ol.rc-list ol.rc-list { list-style: lower-alpha; }
.rc-prose .rc-list .rc-list { margin-top: 0.25rem; margin-bottom: 0; }
.rc-prose .rc-list li {
  margin-top: 0.375rem;
  line-height: 1.65;
}
.rc-prose .rc-list li:first-child { margin-top: 0; }
.rc-prose .rc-list li::marker { color: var(--rc-accent, #3b82f6); font-size: 0.9em; }
.rc-prose .rc-list li > p { margin-bottom: 0.375rem; }
.rc-prose .rc-list li > p:last-child { margin-bottom: 0; }
.rc-prose .rc-list li + li { margin-top: 0.375rem; }

/* Citas: borde lateral discreto y fondo suave, sin bordes completos. */
.rc-quote {
  border-left: 3px solid var(--rc-accent, #3b82f6);
  background: var(--rc-surface-muted, #f3f4f6);
  border-radius: 0 0.375rem 0.375rem 0;
  padding: 0.75rem 1.125rem;
  color: var(--rc-text-muted, #4b5563);
  font-style: italic;
  margin: 1.25rem 0;
}
.rc-quote p { margin-top: 0; margin-bottom: 0; }
.rc-quote p + p { margin-top: 0.5rem; }

/* Separadores sutiles: aportan estructura, no decoracion. */
.rc-rule {
  border: 0;
  border-top: 1px solid var(--rc-border, #e5e7eb);
  margin: 1.75rem 0;
}

.rc-inline-code, .rc-code code {
  font-family: var(--rc-font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
  font-size: 0.9em;
}
.rc-inline-code {
  background: var(--rc-surface-muted, #f3f4f6);
  padding: 0.15em 0.4em;
  border-radius: 0.25rem;
}
.rc-code {
  background: var(--rc-surface-muted, #f3f4f6);
  padding: 1rem 1.25rem;
  border-radius: 0.5rem;
  overflow-x: auto;
  font-size: 0.9rem;
  line-height: 1.6;
  margin: 1.25rem 0;
}

/* Imagenes: se adaptan al ancho, conservan proporcion y no desbordan. */
.rc-figure { margin: 1.5rem 0; }
.rc-figure img {
  width: 100%;
  height: auto;
  border: 1px solid var(--rc-border, #e5e7eb);
  border-radius: 0.5rem;
  display: block;
}
.rc-figcaption {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: var(--rc-text-muted, #6b7280);
  text-align: center;
}

/* Tablas: desplazables en movil sin romper el layout. */
.rc-table-wrap { overflow-x: auto; margin: 1.5rem 0; }
.rc-table { width: 100%; border-collapse: collapse; font-size: 0.9375rem; }
.rc-table th, .rc-table td {
  border: 1px solid var(--rc-border, #e5e7eb);
  padding: 0.5rem 0.75rem;
  text-align: left;
}
.rc-table th { background: var(--rc-surface-muted, #f9fafb); font-weight: 600; }

.rc-faq { display: flex; flex-direction: column; gap: 0.75rem; margin: 1.75rem 0; }
.rc-faq-item {
  border: 1px solid var(--rc-border, #e5e7eb);
  border-radius: 0.5rem;
  padding: 0.875rem 1.125rem;
  background: var(--rc-surface, #ffffff);
}
.rc-faq-question {
  cursor: pointer;
  font-weight: 600;
  color: var(--rc-heading, #0f172a);
  list-style: none;
}
.rc-faq-question::-webkit-details-marker { display: none; }
.rc-faq-question::after { content: " +"; color: var(--rc-accent, #3b82f6); }
.rc-faq-item[open] .rc-faq-question::after { content: " −"; }
.rc-faq-answer { margin-top: 0.625rem; color: var(--rc-text-muted, #4b5563); }
.rc-faq-answer p { margin-top: 0; margin-bottom: 0; }
`;
